import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Card,
  Checkbox,
  EditIcon,
  Modal,
  PlusIcon,
  Search,
  ShowIcon,
  SliderIcon,
  StatusBadge,
  Tooltip,
  TrashIcon,
} from "@forge/common";

import ViewCourseCount from "./view-course-count-card/view-course-count-card";
import LeftPanel from "../common/components/left-panel/left-panel";
import ViewCourseDetail from "./view-course-detail-card/view-course-detail-card";
import Header from "../common/components/header/header";
import FilterAndSort from "../filterandsort/filter-and-sort";
import DuplicateCourse from "../common/components/duplicate-course/duplicateCourse";
import { VIEW_COURSE, VIEW_COURSE_LIST } from "./utils/constants";
import TableListView from "./list-table-view/list-table-view";
import { AppState } from "../../../../common/store/combinedReducer";
import {
  getCourseInstanceViewTemplate,
  getCourseViewTemplate,
  getCardCourseCount,
  deleteCourseTemplate,
  setSelectedCard,
  deleteCourseInstance,
} from "./store/view-course-template.action";
import { useAuth } from "../../../../common/hooks/useAuth";

import "./view-course-list.scss";
import { formatDate } from "../../../../common/utility/plantDetailUtils";
import { REQUIRED_PERMISSIONS } from "../../../../common/authentication/config";
import {
  btnResponsive,
  getResponsiveFontSize,
  textResponsive,
} from "../common/utils/helper";
import ButtonComponent from "../common/components/button/button";
import PanelContainer from "../common/components/panel-container/panel";
import { showToast } from "../store/model/model.action";

const ViewCourseList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  const courseRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [showLeftPanel, setShowLeftPanel] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>("");
  const [filterSearchText, setFilterSearchText] = useState<string>("");
  const [filterBy, setFilterBy] = useState<string>("");
  const [showCopyCourseModal, setShowCopyCourseModal] =
    useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteMultipleModal, setShowDeleteMultipleModal] =
    useState<boolean>(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [courseToDelete, setCourseToDelete] = useState<any>(null);
  const [viewType, setViewType] = useState<string>("grid");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tableContentData, setTableContentData] = useState<any>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pageCount, setPageCount] = useState<number>(1);
  const tableSelectAllRef = useRef<(() => void) | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showCourseInstanceDeleteModal, setShowCourseInstanceDeleteModal] =
    useState(false);
  const [
    showCourseInstanceMultipleDeleteModal,
    setShowCourseInstanceMultipleDeleteModal,
  ] = useState(false);
  const hasReachedEnd = useRef<boolean>(false);

  const {
    viewCourseViewData,
    cardCourseCountData,
    viewCourseInstanceData,
    deleteCourseTemplateData,
    selectedCard,
    deleteCourseInstanceData,
  } = useSelector((state: AppState) => {
    return state.viewCourseTemplateReducer;
  });

  const handleCourseInstanceDeleteClick = (course: any) => {
    setCourseToDelete(course);
    setShowCourseInstanceDeleteModal(true);
  };

  const deleteSingleCourseInstance = () => {
    setShowCourseInstanceDeleteModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseInstance([String(courseToDelete.courseInstanceID)]));
    setCourseToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowCourseInstanceDeleteModal(false);
    setCourseToDelete(null);
  };

  const handleClickHam = () => {
    setShowLeftPanel((prev) => !prev);
  };
  const navigateToCreateCourse = () => {
    navigate("/academy/createCourse");
  };

  const onItemSelect = (data: string, isChecked: boolean) => {
    if (isChecked) {
      if (!selectedItems.includes(data)) {
        setSelectedItems([...selectedItems, data]);
      }
    } else {
      setSelectedItems(selectedItems.filter((item) => item !== data));
    }
  };

  useEffect(() => {
    if (deleteCourseTemplateData && isDeleting) {
      if (deleteCourseTemplateData.status === 200) {
        dispatch(
          showToast({
            type: "success",
            content:
              deleteCourseTemplateData.message ||
              VIEW_COURSE_LIST.TOAST_MESSAGE.SUCCESS_DELETE_MESSAGE,
          })
        );
        // Refresh data based on selectedCard value
        if (selectedCard !== 3) {
          dispatch(
            getCourseViewTemplate({
              pageNumber: 1,
              pageSize: 15,
              courseStage:
                selectedCard === 0
                  ? ""
                  : selectedCard === 1
                    ? "Published"
                    : selectedCard === 2
                      ? "Draft"
                      : "",
            })
          );
        } else {
          dispatch(
            getCourseInstanceViewTemplate({ pageNumber: 1, pageSize: 15 })
          );
        }
        // Refresh count cards
        dispatch(getCardCourseCount());
      } else {
        dispatch(
          showToast({
            type: "error",
            content:
              deleteCourseTemplateData.message ||
              VIEW_COURSE_LIST.TOAST_MESSAGE.ERROR_DELETE_MESSAGE,
          })
        );
      }
      setIsDeleting(false);
    }
  }, [deleteCourseTemplateData]);

  useEffect(() => {
    if (deleteCourseInstanceData && isDeleting) {
      if (deleteCourseInstanceData.status === 200) {
        dispatch(
          showToast({
            type: "success",
            content:
              deleteCourseInstanceData.message ||
              VIEW_COURSE_LIST.INSTANCE_TOAST_MESSAGE.SUCCESS_DELETE_MESSAGE,
          })
        );
        // Refresh data based on selectedCard value
        if (selectedCard !== 3) {
          dispatch(
            getCourseViewTemplate({
              pageNumber: 1,
              pageSize: 15,
              courseStage:
                selectedCard === 0
                  ? ""
                  : selectedCard === 1
                    ? "Published"
                    : selectedCard === 2
                      ? "Draft"
                      : "",
            })
          );
        } else {
          dispatch(
            getCourseInstanceViewTemplate({ pageNumber: 1, pageSize: 15 })
          );
        }
        // Refresh count cards
        dispatch(getCardCourseCount());
      } else {
        dispatch(
          showToast({
            type: "error",
            content:
              deleteCourseInstanceData.message ||
              VIEW_COURSE_LIST.INSTANCE_TOAST_MESSAGE.ERROR_DELETE_MESSAGE,
          })
        );
      }
      setIsDeleting(false);
    }
  }, [deleteCourseInstanceData]);

  useEffect(() => {
    const pageSize = 15;
    setPageCount(1);
    if (selectedCard === 3) {
      dispatch(
        getCourseInstanceViewTemplate({ pageNumber: 1, pageSize: pageSize })
      );
    } else {
      dispatch(
        getCourseViewTemplate({
          pageNumber: 1,
          pageSize: pageSize,
          courseStage:
            selectedCard === 1
              ? "Published"
              : selectedCard === 2
                ? "Draft"
                : "",
        })
      );
    }
  }, [selectedCard]);

  useEffect(() => {
    dispatch(getCardCourseCount());
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (courseRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = courseRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if (isAtBottom && !hasReachedEnd.current) {
          hasReachedEnd.current = true;
          setPageCount((prevCount) => {
            const pageSize = 15;
            const newCount = prevCount + 1;
            if (selectedCard === 3) {
              let totalCount = cardCourseCountData?.find(
                (c: any) => c.id === 3
              )?.count;
              if (
                totalCount &&
                totalCount !==
                viewCourseInstanceData?.courseInstanceData?.length
              ) {
                dispatch(
                  getCourseInstanceViewTemplate({
                    pageNumber: newCount,
                    pageSize: pageSize,
                    prevData: viewCourseInstanceData?.courseInstanceData,
                  })
                );
              }
            } else {
              let totalCount = cardCourseCountData?.find(
                (c: any) => c.id === selectedCard
              )?.count;
              if (totalCount && totalCount !== viewCourseViewData?.length) {
                dispatch(
                  getCourseViewTemplate({
                    pageNumber: newCount,
                    pageSize: pageSize,
                    prevData: viewCourseViewData,
                    courseStage:
                      selectedCard === 1
                        ? "Published"
                        : selectedCard === 2
                          ? "Draft"
                          : "",
                  })
                );
              }
            }
            return newCount;
          });
        } else if (!isAtBottom && hasReachedEnd.current) {
          hasReachedEnd.current = false;
        }
      }
    };

    const courseContainer = courseRef.current;
    if (courseContainer) {
      courseContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (courseContainer) {
        courseContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    selectedCard,
    cardCourseCountData,
    viewCourseViewData,
    viewCourseInstanceData,
  ]);

  const setCourseInstanceData = () => {
    let initialTableData: any[] = [];
    let filteredCourseData = viewCourseInstanceData?.courseInstanceData || [];
    if (searchValue) {
      filteredCourseData = filterArrayByProperty(
        filteredCourseData,
        "courseInstanceName",
        searchValue
      );
    }
    if (filterSearchText && filterBy) {
      let key =
        filterBy.split("course")[0] + "Instance" + filterBy.split("course")[1];
      filteredCourseData = filteredCourseData.filter(
        (course: any) =>
          course[key]?.toLowerCase().includes(filterSearchText.toLowerCase()) ??
          false
      );
    }
    filteredCourseData = handleSort(
      sortBy === "" ? undefined : "asc",
      sortBy,
      filteredCourseData
    );
    filteredCourseData &&
      filteredCourseData.length &&
      filteredCourseData.forEach((course: any, index: number) => {
        if (!course) return;

        let disableRow = course?.inProgress ? course.inProgress : false;
        let failedStatus = course?.status === 3;
        if (course?.expiryDate) {
          const expiryDate = new Date(course.expiryDate!);
          if (expiryDate < new Date()) {
            disableRow = true; // Mark as in-progress if expired
          }
        }
        // Only ACTIVE (status === 1) and FAILED (status === 3) course instances should be selectable
        if (course?.status !== 1 && course?.status !== 3) {
          disableRow = true;
        }
        // For action buttons: only disable for in-progress and expired, NOT for failed
        let disableActionButtons = course?.inProgress ? course.inProgress : false;
        if (course?.expiryDate) {
          const expiryDate = new Date(course.expiryDate!);
          if (expiryDate < new Date()) {
            disableActionButtons = true;
          }
        }
        // For action buttons: allow ACTIVE (status === 1) AND FAILED (status === 3)
        if (course?.status !== 1 && course?.status !== 3) {
          disableActionButtons = true;
        }
        let opacityStyle = disableRow ? "disable-table-data" : "";
        const courseId =
          String(course?.courseInstanceID) || `courseInstance-${index}`;
        const row = {
          courseid: disableRow ? "disabled-row" : courseId,
          cell: [
            {
              content: (
                <div className="flex">
                  <Checkbox
                    id={courseId}
                    className={`table-checkbox bg-[#202020] rounded-none ${disableRow
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                      }`}
                    checked={false}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (disableRow) {
                        e.preventDefault();
                        return;
                      }
                      onItemSelect(courseId, e.target.checked);
                    }}
                  />
                </div>
              ),
            },
            {
              content: disableRow ? (
                <div
                  className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] ${textResponsive(
                    "text-sm"
                  )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                >
                  {course.courseInstanceName}
                </div>
              ) : (
                <Tooltip
                  content={course.courseInstanceName}
                  position="bottom-left"
                >
                  <div
                    className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] ${textResponsive(
                      "text-sm"
                    )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                  >
                    {course.courseInstanceName}
                  </div>
                </Tooltip>
              ),
            },
            {
              content: disableRow ? (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-full w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                >
                  {course.courseInstanceDescription
                    ? course.courseInstanceDescription
                    : "--"}
                </div>
              ) : course.courseInstanceDescription ? (
                <Tooltip
                  content={
                    course.courseInstanceDescription?.length > 100
                      ? `${course.courseInstanceDescription.substring(
                        0,
                        100
                      )}...`
                      : course.courseInstanceDescription
                  }
                  position="bottom-left"
                >
                  <div
                    className={`font-normal ${opacityStyle} ${textResponsive(
                      "text-sm"
                    )} w-full w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                  >
                    {course.courseInstanceDescription}
                  </div>
                </Tooltip>
              ) : (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-full w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                >
                  {course.courseInstanceDescription
                    ? course.courseInstanceDescription
                    : "--"}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`${textResponsive(
                    "text-sm"
                  )} xl-high:w-[190px] xl-mid:w-[160px] xl-high:mt-2 w-[120px] flex items-center`}
                >
                  {course?.inProgress ? (
                    <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-1 space-x-2 py-0.5 items-center in-progress-key xl-high:max-w-[200px] opacity-100">
                      <img src="/progress.svg" alt="." />
                      <p className="text-[#64C3FF]">In-Progress</p>
                    </div>
                  ) : course?.status === 2 ? (
                    <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-1 space-x-2 py-0.5 items-center in-progress-key xl-high:max-w-[200px] opacity-100">
                      <img src="/progress.svg" alt="." />
                      <p className="text-[#64C3FF]">In-progress</p>
                    </div>
                  ) : course?.status === 4 ? (
                    <div className="flex flex-col w-full">
                      <div
                        className={`flex items-center font-normal ml-1 ${opacityStyle}`}
                      >
                        <StatusBadge
                          color="gray"
                          className="mr-2 text-[0.5rem]"
                        />
                        Deleting
                      </div>
                      <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-2 space-x-2 py-0.5 items-center in-progress-key w-full opacity-100">
                        <img src="/progress.svg" alt="." />
                        <p className="text-[#64C3FF]">In-progress</p>
                      </div>
                    </div>
                  ) : course?.status === 3 ? (
                    <div className="border rounded-xl flex justify-center items-center border-1.5 border-[#FF6464] px-2 mt-1 space-x-2 py-0.5 failed-key xl-high:max-w-[200px] opacity-100">
                      <span className="w-2 h-2 bg-[#FF6464] rounded-full"></span>
                      <p className="text-[#FF6464]">Failed</p>
                    </div>
                  ) : (
                    <div
                      className={`flex items-center font-normal ${opacityStyle}`}
                    >
                      <StatusBadge
                        color={course?.status === 1 ? "green" : (course?.status === 0 || course?.status === 2) ? "gray" : "red"}
                        className="mr-1 text-[0.5rem]"
                      />
                      {course?.status === 1 ? "Active" : course?.status === 0 ? "Expired" : course?.status === 2 ? "Deploying" : "Failed"}
                    </div>
                  )}
                </div>
              ),
            },
            {
              content: (
                <div>
                  {course?.courseTemplateAssociated ? (
                    disableRow ? (
                      <p
                        className={`font-normal ${textResponsive(
                          "text-sm"
                        )} ${opacityStyle} line-height-half w-full leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                      >
                        {course.courseTemplateAssociated}
                      </p>
                    ) : (
                      <Tooltip content={course.courseTemplateAssociated} position="bottom-left">
                        <p
                          className={`font-normal ${textResponsive(
                            "text-sm"
                          )} ${opacityStyle} line-height-half w-full leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                        >
                          {course.courseTemplateAssociated}
                        </p>
                      </Tooltip>
                    )
                  ) : (
                    <div
                      className={`font-normal ${opacityStyle} ${textResponsive(
                        "text-sm"
                      )}`}
                    >
                      --
                    </div>
                  )}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-min`}
                >
                  {disableRow ? (
                    <p
                      className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {course?.lastUpdatedBy}
                    </p>
                  ) : (
                    <Tooltip
                      content={course?.lastUpdatedBy}
                      position="bottom-left"
                    >
                      <p
                        className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] xl-high:mb-3 mb-1 truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                      >
                        {course?.lastUpdatedBy}
                      </p>
                    </Tooltip>
                  )}
                  {(() => {
                    if (course.lastUpdatedDate) {
                      return disableRow ? (
                        <p
                          className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                        >
                          {formatDate(course.lastUpdatedDate)}
                        </p>
                      ) : (
                        <Tooltip
                          content={formatDate(course.lastUpdatedDate)}
                          position="bottom-left"
                        >
                          <p
                            className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                          >
                            {formatDate(course.lastUpdatedDate)}
                          </p>
                        </Tooltip>
                      );
                    }
                    return <p>--</p>;
                  })()}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`flex items-center justify-center w-fit space-x-1 ${disableActionButtons
                    ? "action-button-group-disable disable-table-data"
                    : "action-button-group"
                    }`}
                >
                  {hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_VIEW) && (
                    <ButtonComponent
                      id={`view-course-${course?.courseInstanceID}`}
                      data-testid={`view-course-${course?.courseInstanceID}`}
                      aria-label="View"
                      tooltip="View"
                      className="border-0 cursor-pointer rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none m-0.5"
                      iconButton={true}
                      disabled={disableActionButtons}
                      onClick={() =>
                        handleViewCourse(
                          `viewDeployedCourseInstance/${course?.courseInstanceID}`
                        )
                      }
                      size="regular"
                      icon={
                        <ShowIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {hasPermission(REQUIRED_PERMISSIONS.COURSE_INSTANCE_EDIT) && course?.status !== 3 && (
                    <ButtonComponent
                      id={`Edit-course-${course?.courseInstanceID}`}
                      data-testid={`Edit-courseInstance`}
                      aria-label="Edit"
                      tooltip="Edit"
                      className="border-0 cursor-pointer rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none m-0.5"
                      iconButton={true}
                      disabled={disableActionButtons}
                      onClick={() =>
                        handleEditCourseInstanceNavigate(
                          course?.courseInstanceID
                        )
                      }
                      size="regular"
                      icon={
                        <EditIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {hasPermission(
                    REQUIRED_PERMISSIONS.COURSE_INSTANCE_DELETE
                  ) && (
                      <ButtonComponent
                        id={`Delete-course-${course?.courseInstanceID}`}
                        data-testid={`Delete-courseInstance`}
                        aria-label="Delete"
                        tooltip="Delete"
                        disabled={disableActionButtons}
                        className="border-0 cursor-pointer rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none m-0.5"
                        iconButton={true}
                        onClick={() => handleCourseInstanceDeleteClick(course)}
                        size="regular"
                        icon={
                          <TrashIcon className={`${btnResponsive("text-sm")}`} />
                        }
                        iconPosition="left"
                        variant="tertiary"
                      />
                    )}
                </div>
              ),
            },
          ],
        };
        initialTableData.push(row);
      });
    if (initialTableData.length === 0) {
      let noDataMessage: string = "No course instances found";
      noDataMessage = "--";
      setTableContentData([
        {
          courseid: "no-data",
          cell: [
            {
              content: (
                <div className="text-center py-4 text-white">
                  {noDataMessage}
                </div>
              ),
              colSpan: 7, // Span across all columns
            },
          ],
        },
      ]);
    } else {
      setTableContentData(initialTableData);
    }
  };

  const setCourseData = () => {
    let initialTableData: any[] = [];
    let filteredCourseData = viewCourseViewData || [];
    if (searchValue) {
      filteredCourseData = filterArrayByProperty(
        filteredCourseData,
        "courseName",
        searchValue
      );
    }
    if (filterSearchText && filterBy) {
      filteredCourseData = filteredCourseData.filter((course: any) =>
        course[filterBy]?.toLowerCase().includes(filterSearchText.toLowerCase())
      );
    }
    filteredCourseData = handleSort(
      sortBy === "" ? undefined : "asc",
      sortBy,
      filteredCourseData
    );
    filteredCourseData &&
      filteredCourseData.length &&
      filteredCourseData.forEach((course: any, index: number) => {
        let inProgressStatus = [0, 1, 2, 6, 13].includes(course?.courseStage)
          ? true
          : false;
        let failedStatus = [7, 8, 9, 10, 11, 12].includes(course?.courseStage)
          ? true
          : false;
        // Separate variable for opacity styling that includes deleting state
        let shouldApplyOpacity = inProgressStatus || failedStatus || course?.courseStage === 16 || course?.courseStage === 17;
        let opacityStyle = shouldApplyOpacity ? "disable-table-data" : "";
        const courseId = course?.courseId || `course-${index}`;
        const row = {
          courseid:
            inProgressStatus ? "disabled-row" : courseId,
          cell: [
            {
              content: (
                <div className="flex">
                  <Checkbox
                    data-testid={courseId}
                    id={courseId}
                    className={`table-checkbox bg-[#202020] ${inProgressStatus
                      ? "opacity-30 cursor-disable"
                      : "cursor-pointer"
                      }`}
                    checked={false}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (inProgressStatus) {
                        e.preventDefault();
                        return;
                      }
                      onItemSelect(courseId, e.target.checked);
                    }}
                  />
                </div>
              ),
            },
            {
              content:
                inProgressStatus || failedStatus ? (
                  <div
                    className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:[180px] xl-high:w-[250px] ${textResponsive(
                      "text-sm"
                    )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                  >
                    {course?.courseName}
                  </div>
                ) : (
                  <Tooltip content={course?.courseName || ''} position="bottom-left">
                    <div
                      className={`font-normal text-[#45A7EA] ${opacityStyle} w-[100px] xl-low:w-[150px] xl-mid:[180px] xl-high:w-[250px] ${textResponsive(
                        "text-sm"
                      )} truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {course?.courseName}
                    </div>
                  </Tooltip>
                ),
            },
            {
              content:
                inProgressStatus || failedStatus ? (
                  <div
                    className={`font-normal ${opacityStyle} ${textResponsive(
                      "text-sm"
                    )} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[250px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                  >
                    {course?.courseDescription}
                  </div>
                ) : (
                  <Tooltip
                    content={
                      course?.courseDescription && course.courseDescription.length > 250
                        ? `${course.courseDescription.substring(0, 250)}...`
                        : course?.courseDescription || ''
                    }
                    position="bottom-left"
                  >
                    <div
                      className={`font-normal ${opacityStyle} ${textResponsive(
                        "text-sm"
                      )} w-[100px] xl-low:w-[150px] xl-mid:w-[180px] xl-high:w-[220px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                    >
                      {course?.courseDescription
                        ? course?.courseDescription
                        : "--"}
                    </div>
                  </Tooltip>
                ),
            },
            {
              content: (
                <div
                  className={`${textResponsive(
                    "text-sm"
                  )} xl-high:w-[190px] xl-mid:w-[160px] xl-high:mt-2 w-[120px] flex items-center`}
                >
                  {inProgressStatus ? (
                    <div className="flex flex-col w-full">
                      <div
                        className={`flex items-center font-normal ml-1 ${opacityStyle}`}
                      >
                        <StatusBadge
                          color="gray"
                          className="mr-2 text-[0.5rem]"
                        />
                        In-Progress
                      </div>
                      <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-2 space-x-2 py-0.5 items-center in-progress-key w-full opacity-100">
                        <img src="/progress.svg" alt="." />
                        <p className="text-[#64C3FF]">In-Progress</p>
                      </div>
                    </div>
                  ) : course?.courseStage === 16 || course?.courseStage === 17 ? (
                    <div className="flex flex-col w-full">
                      <div className="border rounded-xl flex justify-center border-1.5 border-[#64C3FF] px-2 mt-1 space-x-2 py-0.5 items-center in-progress-key w-full opacity-100">
                        <img src="/progress.svg" alt="." />
                        <p className="text-[#64C3FF]">In-Progress</p>
                      </div>
                      <div
                        className={`flex items-center font-normal ml-1 mt-2 ${opacityStyle}`}
                      >
                        <StatusBadge
                          color="gray"
                          className="mr-2 text-[0.5rem]"
                        />
                        Deleting
                      </div>
                    </div>
                  ) : failedStatus ? (
                    <div className="flex flex-col w-full">
                      <div
                        className={`flex items-center font-normal ml-1 ${opacityStyle}`}
                      >
                        <StatusBadge
                          color="red"
                          className="mr-2 text-[0.5rem]"
                        />
                        Failed
                      </div>
                      <div className="border rounded-xl flex justify-center border-1.5 border-[#FF6464] px-2 mt-2 space-x-2 py-0.5 items-center failed-key w-full opacity-100">
                        <span className="w-2 h-2 bg-[#FF6464] rounded-full"></span>
                        <p className="text-[#FF6464]">Failed</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`flex items-center font-normal ${opacityStyle}`}
                    >
                      <StatusBadge
                        color={
                          course?.courseStage === 3 ? "orange" :
                            course?.courseStage === 4 ? "green" :
                              [0, 1, 2, 6, 13].includes(course?.courseStage) ? "gray" :
                                [7, 8, 9, 10, 11, 12].includes(course?.courseStage) ? "red" :
                                  course?.courseStage === 16 || course?.courseStage === 17 ? "gray" : "gray"
                        }
                        className="mr-2 text-[0.5rem]"
                      />
                      {
                        course?.courseStage === 3 ? "Draft" :
                          course?.courseStage === 4 ? "Published" :
                            [0, 1, 2, 6, 13].includes(course?.courseStage) ? "In-progress" :
                              [7, 8, 9, 10, 11, 12].includes(course?.courseStage) ? "Failed" :
                                course?.courseStage === 16 || course?.courseStage === 17 ? "Deleting" : "Unknown"
                      }
                    </div>
                  )}
                </div>
              ),
            },
            {
              content:
                inProgressStatus || failedStatus ? (
                  <p
                    className={`font-normal ${textResponsive(
                      "text-sm"
                    )} ${opacityStyle} line-height-half w-[80px] xl-low:w-[80px] xl-mid:w-[180px] xl-high:w-[220px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                  >
                    {course?.vAppTemplateName}
                  </p>
                ) : (
                  <Tooltip
                    content={course?.vAppTemplateName || ''}
                    position="bottom-left"
                  >
                    <p
                      className={`font-normal ${textResponsive(
                        "text-sm"
                      )} ${opacityStyle} line-height-half w-[80px] xl-low:w-[80px] xl-mid:w-[180px] xl-high:w-[220px] leading-[22px] break-all overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]`}
                    >
                      {course?.vAppTemplateName}
                    </p>
                  </Tooltip>
                ),
            },
            {
              content: (
                <div
                  className={`font-normal ${opacityStyle} ${textResponsive(
                    "text-sm"
                  )} w-min`}
                >
                  {inProgressStatus || failedStatus ? (
                    <p
                      className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {course?.lastModifiedBy}
                    </p>
                  ) : (
                    <Tooltip
                      content={course?.lastModifiedBy || ''}
                      position="bottom-left"
                    >
                      <p
                        className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] xl-high:mb-3 mb-1 truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                      >
                        {course?.lastModifiedBy}
                      </p>
                    </Tooltip>
                  )}
                  {(() => {
                    if (course?.lastModifiedDate) {
                      return inProgressStatus || failedStatus ? (
                        <p
                          className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                        >
                          {formatDate(course.lastModifiedDate)}
                        </p>
                      ) : (
                        <Tooltip
                          content={formatDate(course.lastModifiedDate)}
                          position="bottom-left"
                        >
                          <p
                            className={`w-[130px] xl-low:w-[150px] xl-mid:w-[200px] xl-high:w-[300px] truncate overflow-hidden text-ellipsis whitespace-nowrap`}
                          >
                            {formatDate(course.lastModifiedDate)}
                          </p>
                        </Tooltip>
                      );
                    }
                    return "--";
                  })()}
                </div>
              ),
            },
            {
              content: (
                <div
                  className={`flex items-center justify-center w-fit space-x-1 ${shouldApplyOpacity
                    ? "action-button-group-disable disable-table-data"
                    : "action-button-group"
                    }`}
                >
                  {hasPermission(REQUIRED_PERMISSIONS.COURSE_LIBRARY_VIEW) && (
                    <ButtonComponent
                      id={`view-course-${courseId}`}
                      data-testid={`view-course-${courseId}`}
                      aria-label="View"
                      className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                      iconButton={true}
                      disabled={shouldApplyOpacity}
                      onClick={() =>
                        handleViewCourse(`viewCourse/${course?.courseId}`)
                      }
                      size="regular"
                      tooltip="View"
                      icon={
                        <ShowIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {[0, 1, 2, 3].includes(Number(course?.courseStage)) && course?.status !== 3 && (
                    <ButtonComponent
                      id={`Edit-course-${courseId}`}
                      data-testid={`Edit-course-${courseId}`}
                      aria-label="Edit"
                      className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                      iconButton={true}
                      disabled={shouldApplyOpacity}
                      onClick={() =>
                        navigate("/academy/editCourse/" + courseId.toString())
                      }
                      size="regular"
                      tooltip="Edit"
                      icon={
                        <EditIcon className={`${btnResponsive("text-sm")}`} />
                      }
                      iconPosition="left"
                      variant="tertiary"
                    />
                  )}
                  {hasPermission(
                    REQUIRED_PERMISSIONS.COURSE_LIBRARY_DELETE
                  ) && (
                      <ButtonComponent
                        id={`Delete-course-${courseId}`}
                        data-testid={`Delete-course-${courseId}`}
                        aria-label="Delete"
                        className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                        iconButton={true}
                        disabled={shouldApplyOpacity}
                        onClick={() => handleDeleteCourse(course)}
                        size="regular"
                        tooltip="Delete"
                        icon={
                          <TrashIcon className={`${btnResponsive("text-sm")}`} />
                        }
                        iconPosition="left"
                        variant="tertiary"
                      />
                    )}
                  {course.courseStage === 4 &&
                    hasPermission(
                      REQUIRED_PERMISSIONS.COURSE_INSTANCE_DEPLOY
                    ) && (
                      <ButtonComponent
                        id={`Deploy-course-${courseId}`}
                        data-testid={`Deploy-course-${courseId}`}
                        aria-label="Deploy"
                        tooltip="Deploy"
                        className="border-0 rounded-none border-none !border-0 shadow-none focus:ring-0 outline-none"
                        iconButton={true}
                        disabled={shouldApplyOpacity}
                        onClick={() => {
                          navigate("/academy/startCourseInstance", {
                            state: { courseId: course.courseId },
                          });
                        }}
                        size="regular"
                        icon={
                          <img
                            src="/play-white.svg"
                            className={`${btnResponsive("text-sm")}`}
                          />
                        }
                        iconPosition="left"
                        variant="tertiary"
                      />
                    )}
                </div>
              ),
            },
          ],
        };
        initialTableData.push(row);
      });
    if (initialTableData.length === 0) {
      setTableContentData([
        {
          courseid: "no-data",
          cell: [
            {
              content: <div className="text-center py-4 text-white">--</div>,
              colSpan: 7, // Span across all columns
            },
          ],
        },
      ]);
    } else {
      setTableContentData(initialTableData);
    }
  };

  useEffect(() => {
    if (selectedCard === 3) {
      setCourseInstanceData();
    } else {
      setCourseData();
    }
  }, [
    viewCourseViewData,
    filterSearchText,
    filterBy,
    selectedCard,
    searchValue,
    viewCourseInstanceData?.courseInstanceData,
    sortBy,
  ]);

  const filterArrayByProperty = (
    data: any[],
    propertyName: string,
    searchValue: string
  ): any[] => {
    if (!searchValue || !propertyName) return data;
    return data.filter(
      (item: any) =>
        item[propertyName]?.toLowerCase().includes(searchValue.toLowerCase()) ??
        false
    );
  };

  const handleSelectAll = () => {
    // If viewType is list and we have a reference to the table's selectAll function, use it
    if (viewType === "list" && tableSelectAllRef.current) {
      tableSelectAllRef.current();
      return;
    }

    // Otherwise, use the existing grid logic
    const allIds: any = [];
    let data = [];
    if (selectedCard === 3) {
      data = viewCourseInstanceData?.courseInstanceData || [];
      // Only select course instances that are not disabled (not in-progress and not expired)
      data.forEach((course: any) => {
        if (!course) return;

        let disableRow = false;
        if (course?.inProgress) {
          disableRow = true;
        } else if (course?.expiryDate) {
          const expiryDate = new Date(course.expiryDate);
          if (expiryDate < new Date()) {
            disableRow = true;
          }
        }
        const courseId = course?.courseInstanceID;
        // Only include ACTIVE and FAILED instances
        if (
          !disableRow &&
          (course?.status === 1 || course?.status === 3) &&
          courseId &&
          String(courseId) !== "disabled-row" &&
          String(courseId) !== "no-data"
        ) {
          allIds.push(String(courseId));
        }
      });
    } else {
      data = viewCourseViewData || [];
      data.forEach((course: any) => {
        if (!course) return;

        const courseId = course?.courseId || course?.courseInstanceID;
        const inProgressStatus = [0, 1, 2, 6, 13].includes(course?.courseStage);
        const failedStatus = [7, 8, 9, 10, 11, 12].includes(
          course?.courseStage
        );
        if (
          courseId !== "disabled-row" &&
          courseId !== "no-data" &&
          !inProgressStatus
        ) {
          allIds.push(courseId);
        }
      });
    }

    if (viewType === "grid") {
      // Use the same courseId mapping logic as the grid view component
      const currentlySelectedAvailable = selectedItems.filter((item) =>
        allIds.includes(item)
      );
      if (
        currentlySelectedAvailable.length === allIds.length &&
        allIds.length > 0
      ) {
        setSelectedItems([]);
      } else {
        setSelectedItems(allIds);
      }
    } else {
      setSelectedItems(allIds);
    }
  };

  const handleCloseFilterModal = () => setShowFilterModal(false);

  const handleViewCourse = (courseId: string) => {
    if (courseId) {
      navigate(`/academy/${courseId}`);
    }
  };

  function getIdName(id: string) {
    const idMap: { [key: string]: string } = {
      "Course Name": "courseName",
      Description: "courseDescription",
      "VMS Linked": "vAppTemplateName",
      "Last Modified Date": "lastModifiedDate",
      "Last Modified By": "lastModifiedBy",
    };
    return idMap[id] || id;
  }

  const handleSort = (direction: "asc" | undefined, id: string, data: any) => {
    if (!data || data.length === 0) return data;
    if (data && data.length > 0 && id !== "") {
      let keyId = getIdName(id);

      if (direction === undefined) {
        return data; // Return original data if no sorting direction is provided
      } else {
        const sortedData = data.sort((a: any, b: any) => {
          const aValue = a[keyId]?.toString();
          const bValue = b[keyId]?.toString();

          return aValue?.localeCompare(bValue || "") || 0;
        });
        return sortedData;
      }
    }
    return data;
  };

  const handleCardChange = (cardId: number) => {
    dispatch(setSelectedCard(cardId));
    setSelectedItems([]);
    setViewType("grid");
  };

  const handleDeleteCourse = (course: any) => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  const handleEditCourseInstanceNavigate = (courseId: number) => {
    navigate("/academy/editCourseInstance/" + courseId.toString());
  };

  const confirmDeleteCourse = () => {
    setShowDeleteModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseTemplate([Number(courseToDelete.courseId)]));
    setCourseToDelete(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) {
      return;
    }
    if (selectedCard === 3) {
      setShowCourseInstanceMultipleDeleteModal(true);
    } else {
      setShowDeleteMultipleModal(true);
    }
  };

  const getTotalCount = () => {
    let totalCount = 0;
    let loadedCount = 0;
    
    totalCount = cardCourseCountData?.find(
      (o: any) => o?.id === selectedCard
    )?.count || 0;
    
    if (selectedCard === 3) {
      loadedCount = viewCourseInstanceData?.courseInstanceData?.length || 0;
    } else {
      loadedCount = viewCourseViewData?.length || 0;
    }
    
    return { loadedCount, totalCount };
  };

  const getTitle = () => {
    switch (selectedCard) {
      case 0:
        return VIEW_COURSE_LIST.TITLES.COURSE_ENVIRONMENTS;
      case 1:
        return VIEW_COURSE_LIST.TITLES.TEMPLATES;
      case 2:
        return VIEW_COURSE_LIST.TITLES.DRAFTS;
      case 3:
        return VIEW_COURSE_LIST.TITLES.DEPLOYED_COURSES;
      default:
        return VIEW_COURSE_LIST.TITLES.TEMPLATES;
    }
  };

  const getSelectedCourseNames = () => {
    let selectedCourseName: string[] = [];
    viewCourseViewData &&
      viewCourseViewData.map((course: any) => {
        if (selectedItems.includes(course?.courseId)) {
          selectedCourseName.push(course?.courseName);
        }
      });
    return selectedCourseName;
  };

  const getSelectedCourseTemplateAssociated = () => {
    const selectedTemplateAssociatedSet = new Set<string>();
    if (selectedCard === 3) {
      (viewCourseInstanceData?.courseInstanceData || []).forEach(
        (course: any) => {
          const id = String(course?.courseInstanceID);
          if (
            selectedItems.includes(id) &&
            course?.courseInstanceName &&
            course?.courseTemplateAssociated
          ) {
            selectedTemplateAssociatedSet.add(course.courseTemplateAssociated);
          }
        }
      );
    }
    return Array.from(selectedTemplateAssociatedSet);
  };

  const getSelectedCourseInstanceNames = () => {
    const selectedInstanceNames: string[] = [];
    if (selectedCard === 3) {
      (viewCourseInstanceData?.courseInstanceData || []).forEach(
        (course: any) => {
          const id = String(course?.courseInstanceID);
          if (selectedItems.includes(id)) {
            if (course?.courseInstanceName) {
              selectedInstanceNames.push(course.courseInstanceName);
            }
          }
        }
      );
    }
    return selectedInstanceNames;
  };

  const confirmDeleteMultipleCourses = () => {
    setShowDeleteMultipleModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseTemplate(selectedItems.map((id) => Number(id))));
    setSelectedItems([]);
  };

  const deleteMultipleCourseInstance = () => {
    setShowCourseInstanceMultipleDeleteModal(false);
    setIsDeleting(true);
    dispatch(deleteCourseInstance(selectedItems.map((id) => String(id))));
    setSelectedItems([]);
  };

  const handleCloseDeleteMultipleModal = () => {
    setShowDeleteMultipleModal(false);
  };
  const handleMultipleDeleteCancel = () => {
    setShowCourseInstanceMultipleDeleteModal(false);
  };

  return (
    <PanelContainer title={VIEW_COURSE.LIBRARY_WELCOME_TEXT}>
      <div
        style={{ height: "calc(100vh - 100px)" }}
        ref={courseRef}
        className="overflow-y-auto"
      >
        <ViewCourseCount
          cardCourseCountData={cardCourseCountData}
          selectedCard={selectedCard}
          setSelectedCard={(cardId: number) => {
            handleCardChange(cardId);
          }}
        />
        <div className="view-course-detail">
          <Card className="!mb-5 !rounded-none !flex !flex-col !h-auto">
            <div className="title-container flex justify-between items-center p-4">
              <div
                className={`${getResponsiveFontSize(
                  20
                )} font-medium leading-[34px] text-white`}
              >
                <span>{getTitle()} | </span>
                <span
                  className={`${textResponsive(
                    "text-base"
                  )} font-medium leading-[22px]`}
                >
                  {(() => {
                    const { loadedCount, totalCount } = getTotalCount();
                    return `${loadedCount}/${totalCount} ${VIEW_COURSE_LIST.TOTAL_LABEL}`;
                  })()}
                </span>
              </div>
              {selectedCard !== 3 &&
                hasPermission("dp.twin.course.library.create") && (
                  <ButtonComponent
                    className="xl-high:w-[360px]"
                    data-testid="create-new-course-button"
                    onClick={navigateToCreateCourse}
                    size="regular"
                    icon={
                      <PlusIcon className={`${btnResponsive("text-base")}`} />
                    }
                    iconPosition="right"
                    variant="primary"
                    title={VIEW_COURSE_LIST.CREATE_NEW_COURSE}
                  />
                )}
            </div>
            <div className="flex p-4 justify-between items-center">
              <div className="flex items-center gap-2 relative">
                <Search
                  className="search-bar h-[34.864px] xl-high:h-[45px]"
                  aria-label="Search"
                  debounce={100}
                  id="course-search"
                  data-testid="course-search"
                  defaultValue={searchValue}
                  minCharacters={1}
                  onChange={(val: any) => setSearchValue(val?.target?.value)}
                  placeholder={VIEW_COURSE_LIST.SEARCH_PLACEHOLDER}
                  size="large"
                />
                <ButtonComponent
                  aria-label="Filter and Sort"
                  className={`filter-button xl-high:w-[200px] font-normal rounded text-white`}
                  data-testid="filter-sort-button"
                  onClick={() => setShowFilterModal((prev) => !prev)}
                  size="regular"
                  icon={
                    <SliderIcon className={`${btnResponsive("text-sm")}`} />
                  }
                  iconPosition="left"
                  variant="tertiary"
                  title={VIEW_COURSE_LIST.FILTER_SORT}
                />
                <FilterAndSort
                  open={showFilterModal}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  setFilterBy={setFilterBy}
                  setFilterSearchText={setFilterSearchText}
                  onClose={handleCloseFilterModal}
                />
              </div>
              <div className="flex items-center gap-2">
                <ButtonComponent
                  aria-label="List View"
                  data-testid="list-view-button"
                  className={`text-white filter-button font-normal rounded`}
                  iconButton={true}
                  icon={
                    viewType === "list" ? (
                      <img
                        src="/list-white.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    ) : (
                      <img
                        src="/list-gray.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    )
                  }
                  onClick={() => {
                    setViewType("list");
                    setSelectedItems([]);
                  }}
                  iconPosition="left"
                  size="regular"
                  variant="tertiary"
                />
                <ButtonComponent
                  aria-label="Grid View"
                  data-testid="grid-view-button"
                  className={`text-white filter-button font-normal rounded`}
                  icon={
                    viewType === "grid" ? (
                      <img
                        src="/grid-white.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    ) : (
                      <img
                        src="/grid-gray.svg"
                        alt=""
                        className={`${btnResponsive("icons")}`}
                      />
                    )
                  }
                  iconButton={true}
                  onClick={() => {
                    setViewType("grid");
                    setSelectedItems([]);
                  }}
                  iconPosition="left"
                  size="regular"
                  variant="tertiary"
                />
                <ButtonComponent
                  aria-label="Delete Selected"
                  data-testid="delete-selected-button"
                  className={`text-white filter-button font-normal rounded`}
                  onClick={handleDeleteSelected}
                  size="regular"
                  variant="tertiary"
                  title={VIEW_COURSE_LIST.DELETE_SELECTED}
                  disabled={!selectedItems.length}
                />
                {/* <Button className={`filter-button text-white ${btnResponsive()} font-normal rounded w-[120px] xl-mid:w-[160px] xl-high:w-[200px]`}
                      data-testid="select-all-button"
                      variant="tertiary" size="regular" onClick={() => handleSelectAll()} aria-label="Select All">
                      {(() => {
                        if (viewType === "list") {
                          const selectableIds: string[] = [];
                          tableContentData.forEach((item: any) => {
                            if (item.courseid !== "disabled-row" && item.courseid !== "no-data") {
                              selectableIds.push(item.courseid);
                            }
                          });
                          const currentlySelectedInTable = selectedItems.filter(item => selectableIds.includes(item));
                          return currentlySelectedInTable.length === selectableIds.length && selectableIds.length > 0
                            ? VIEW_COURSE_LIST.DESELECT_ALL
                            : VIEW_COURSE_LIST.SELECT_ALL;
                        }

                        // For grid view, fix for course instance (selectedCard === 3)
                        let data = [];
                        if (selectedCard === 3) {
                          data = viewCourseInstanceData?.courseInstanceData || [];
                          // Only select course instances that are not disabled (not in-progress and not expired)
                          const availableIds: string[] = [];
                          data.forEach((course: any) => {
                            if (!course) return;
                            
                            let disableRow = false;
                            if (course?.inProgress) {
                              disableRow = true;
                            } else if (course?.expiryDate) {
                              const expiryDate = new Date(course.expiryDate);
                              if (expiryDate < new Date()) {
                                disableRow = true;
                              }
                            }
                            const courseId = course?.courseInstanceID;
                            // Only include ACTIVE instances
                            if (!disableRow && course?.status === 1 && courseId && String(courseId) !== "disabled-row" && String(courseId) !== "no-data") {
                              availableIds.push(String(courseId));
                            }
                          });
                          const currentlySelectedAvailable = selectedItems.filter(item => availableIds.includes(item));
                          return currentlySelectedAvailable.length === availableIds.length && availableIds.length > 0
                            ? VIEW_COURSE_LIST.DESELECT_ALL
                            : VIEW_COURSE_LIST.SELECT_ALL;
                        } else {
                          data = viewCourseViewData ? viewCourseViewData : [];
                        }

                        if (searchValue) {
                          data = filterArrayByProperty(data, 'courseName', searchValue);
                        }
                        if (filterSearchText && filterBy) {
                          data = data.filter((course: any) => 
                            course[filterBy]?.toLowerCase().includes(filterSearchText.toLowerCase())
                          );
                        }

                        const availableIds: string[] = [];
                        data.forEach((course: any, originalIndex: number) => {
                          if (!course) return;
                          
                          const isInProgress = [0, 1, 2, 6, 7, 8, 9, 10, 11, 12, 13].includes(course?.courseStage);
                          if (!isInProgress) {
                            const courseId = course?.courseId || course?.CourseID || course?.courseDefintionID || course?.CourseInstanceID || `course-${originalIndex}`;
                            availableIds.push(courseId);
                          }
                        });
                        const currentlySelectedAvailable = selectedItems.filter(item => availableIds.includes(item));
                        return currentlySelectedAvailable.length === availableIds.length && availableIds.length > 0
                          ? VIEW_COURSE_LIST.DESELECT_ALL
                          : VIEW_COURSE_LIST.SELECT_ALL;
                      })()}
                    </Button> */}
              </div>
            </div>
            <div className="course-detail-container">
              {(() => {
                let filteredCourseData = viewCourseViewData
                  ? viewCourseViewData
                  : [];
                if (selectedCard === 3) {
                  filteredCourseData =
                    viewCourseInstanceData?.courseInstanceData || [];
                  const expireData = (expiryDate: string) => {
                    if (expiryDate) {
                      const expiryDateObj = new Date(expiryDate);
                      if (expiryDateObj < new Date()) {
                        return true; // Mark as in-progress if expired
                      }
                    }
                    return false;
                  };
                  filteredCourseData = Array.isArray(filteredCourseData)
                    ? filteredCourseData.map((course: any) => ({
                      courseId: course?.courseInstanceID
                        ? String(course.courseInstanceID)
                        : "",
                      courseName: course?.courseInstanceName,
                      courseDescription: course?.courseInstanceDescription,
                      vAppTemplateName: course?.courseTemplateAssociated,
                      lastModifiedBy: course?.lastUpdatedBy,
                      lastModifiedDate: course?.lastUpdatedDate,
                      inProgressStatus: expireData(course?.expiryDate),
                      expiryData: course?.expiryDate,
                      isEmailSent: course?.isEmailSent,
                      status: course?.status,
                    }))
                    : [];
                } else {
                  filteredCourseData = Array.isArray(filteredCourseData)
                    ? filteredCourseData.map((course: any) => ({
                      courseId: course?.courseId || "",
                      courseName: course?.courseName,
                      courseDescription: course?.courseDescription,
                      vAppTemplateName: course?.vAppTemplateName,
                      lastModifiedBy: course?.lastModifiedBy,
                      lastModifiedDate: course?.lastModifiedDate,
                      courseStage: course?.courseStage,
                      inProgressStatus: [
                        0, 1, 2, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17
                      ].includes(course?.courseStage),
                    }))
                    : [];
                }
                if (searchValue) {
                  filteredCourseData = filterArrayByProperty(
                    filteredCourseData,
                    "courseName",
                    searchValue
                  );
                }
                if (filterSearchText && filterBy) {
                  filteredCourseData = filteredCourseData.filter(
                    (course: any) =>
                      course[filterBy]
                        ?.toLowerCase()
                        .includes(filterSearchText.toLowerCase())
                  );
                }
                filteredCourseData = handleSort(
                  sortBy === "" ? undefined : "asc",
                  sortBy,
                  filteredCourseData
                );
                if (filteredCourseData.length === 0) {
                  return (
                    <div className="text-center flex items-center justify-center text-white no-data-found">
                      {
                        selectedCard === 2 ? (<p>No draft courses found</p>) :
                          selectedCard === 1 ? (<p>No templates found</p>) :
                            selectedCard === 0 ? (<p>No course environments found</p>) : (<p>No deployed courses found</p>)
                      }
                    </div>
                  );
                } else {
                  return viewType === "grid" ? (
                    <ViewCourseDetail
                      selectedCard={selectedCard}
                      courseData={filteredCourseData}
                      setSelectedItems={setSelectedItems}
                      selectedItems={selectedItems}
                    />
                  ) : (
                    <TableListView
                      courseData={tableContentData}
                      selectedCard={selectedCard}
                      setSelectedItems={setSelectedItems}
                      selectedItems={selectedItems}
                      onSelectAllRef={tableSelectAllRef}
                    />
                  );
                }
              })()}
            </div>
            {showCopyCourseModal && (
              <DuplicateCourse
                isOpen={showCopyCourseModal}
                courseId={selectedCourseId}
                onClose={() => {
                  setShowCopyCourseModal(false);
                  setSelectedCourseId("");
                }}
              />
            )}
            {showDeleteModal && (
              <Modal
                backdrop
                onModalClose={handleCloseDeleteModal}
                className="border border-[#E0E0E0] border-[0.2px]"
                title="Delete Course Template"
              >
                <div>
                  <p className="mb-4">
                    {VIEW_COURSE.DELETE.DELETE_COURSE_MESSAGE_LINE1} "
                    {courseToDelete?.courseName}"?{" "}
                  </p>
                  <p>{VIEW_COURSE.DELETE.DELETE_COURSE_MESSAGE_LINE2}</p>
                  <div className="flex gap-3 justify-end pt-4">
                    <Button
                      id="cancel-delete-button"
                      data-testid="cancel-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleCloseDeleteModal}
                    >
                      {VIEW_COURSE.DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-delete-button"
                      data-testid="confirm-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={confirmDeleteCourse}
                    >
                      {VIEW_COURSE.DELETE.DELETE_ANYWAY}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
            {showDeleteMultipleModal && (
              <Modal
                backdrop
                onModalClose={handleCloseDeleteMultipleModal}
                className="border border-[#E0E0E0] border-[0.2px]"
                title={VIEW_COURSE.MULTIPLE_DELETE.TITLE}
              >
                <div>
                  <p className="mb-4">
                    {
                      VIEW_COURSE.MULTIPLE_DELETE
                        .MULTIPLE_DELETE_COURSE_MESSAGE_LINE1
                    }{" "}
                    {getSelectedCourseNames().length} template(s)?
                  </p>
                  <p className="mb-4">
                    {
                      VIEW_COURSE.MULTIPLE_DELETE
                        .MULTIPLE_DELETE_COURSE_MESSAGE_LINE2
                    }
                  </p>
                  <div className="max-h-48 overflow-y-auto p-3 mb-4">
                    <ul className="space-y-2">
                      {getSelectedCourseNames().map((courseName, index) => (
                        <li
                          key={index}
                          className="text-sm text-gray-300 border-b border-gray-700 pb-1"
                        >
                          {courseName}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-3 justify-end pt-4">
                    <Button
                      id="cancel-multiple-delete-button"
                      data-testid="cancel-multiple-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleCloseDeleteMultipleModal}
                    >
                      {VIEW_COURSE.MULTIPLE_DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-multiple-delete-button"
                      data-testid="confirm-multiple-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={confirmDeleteMultipleCourses}
                    >
                      {VIEW_COURSE.MULTIPLE_DELETE.DELETE_TEMPLATES}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
            {/* Delete Course Instance Confirmation Modal */}
            {showCourseInstanceDeleteModal && (
              <Modal
                backdrop
                onModalClose={handleDeleteCancel}
                className="border border-[#E0E0E0] border-[0.2px]"
                title="Delete Deployed Course"
              >
                <div>
                  <div className="text-sm text-white-700 mb-1 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE1} "
                    {courseToDelete?.courseInstanceName}",{" "}
                  </div>
                  <div className="text-sm text-white-700 mb-3 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE2}
                  </div>
                  <div className="text-sm text-white-700 mb-6 text-left">
                    "{courseToDelete?.courseTemplateAssociated}"
                  </div>
                  <div className="text-sm text-white-700 mb-12 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE3}
                  </div>
                  <div className="flex justify-center gap-4">
                    <Button
                      id="cancel-instance-delete-button"
                      data-testid="cancel-instance-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleDeleteCancel}
                      className="min-w-[160px] text-sm font-normal"
                    >
                      {VIEW_COURSE.DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-instance-delete-button"
                      data-testid="confirm-instance-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={deleteSingleCourseInstance}
                      className="min-w-[160px] text-sm font-normal"
                    >
                      {VIEW_COURSE.DELETE.FORCE_DELETE}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}

            {/* Delete Course Instance Confirmation Modal */}
            {showCourseInstanceMultipleDeleteModal && (
              <Modal
                backdrop
                onModalClose={handleMultipleDeleteCancel}
                className="border border-[#E0E0E0] border-[0.2px]"
                title="Delete Deployed Courses"
              >
                <div>
                  <div className="text-sm text-white-700 mb-1 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE1}
                  </div>
                  <div className="max-h-48 overflow-y-auto p-3 mb-4">
                    <ul className="space-y-2">
                      {getSelectedCourseInstanceNames().map(
                        (courseInstanceName, index) => (
                          <li
                            key={index}
                            className="text-sm text-white-700 border-b border-gray-700 pb-1"
                          >
                            {courseInstanceName}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="text-sm text-white-700 mb-3 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE2}
                  </div>
                  <div className="max-h-48 overflow-y-auto p-3 mb-4">
                    <ul className="space-y-2">
                      {getSelectedCourseTemplateAssociated().map(
                        (courseTemplateAssociated, index) => (
                          <li
                            key={index}
                            className="text-sm text-white-700 border-b border-gray-700 pb-1"
                          >
                            {courseTemplateAssociated}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="text-sm text-white-700 mb-12 text-left">
                    {VIEW_COURSE.DELETE.DELETE_INSTANCE_MESSAGE_LINE3}
                  </div>
                  <div className="flex justify-center gap-4">
                    <Button
                      id="cancel-multiple-instance-delete-button"
                      data-testid="cancel-multiple-instance-delete-button"
                      variant="secondary"
                      size="regular"
                      onClick={handleMultipleDeleteCancel}
                      className="min-w-[160px] text-sm font-normal"
                      aria-label="Cancel Delete Multiple Instances"
                    >
                      {VIEW_COURSE.DELETE.CANCEL}
                    </Button>
                    <Button
                      id="confirm-multiple-instance-delete-button"
                      data-testid="confirm-multiple-instance-delete-button"
                      variant="primary"
                      size="regular"
                      onClick={deleteMultipleCourseInstance}
                      className="min-w-[160px] text-sm font-normal"
                      aria-label="Confirm Delete Multiple Instances"
                    >
                      {VIEW_COURSE.DELETE.FORCE_DELETE}
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
          </Card>
        </div>
      </div>
    </PanelContainer>
  );
};

export default ViewCourseList;
